import { useState } from 'react';
import { CalendarDays, Clock, MapPin, CircleX } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCoachActivities } from '@/data/coachActivity';
import { Badge } from '@/components/ui/badge';
import {
  useAvailabilitySelf,
  useDeleteAvailability
} from '@/data/availability';
import { Button } from '@/components/ui/button';
import {
  getDateWithTimeZone,
  getTimeWithTimeZone
} from '@/helpers/dateHelpers';
import EditAvailabilityForm from './edit/EditAvailabilityForm';

const AvailabilityCard = ({ availability }) => {
  const [isEditing, setIsEditing] = useState(false);
  const coachActivities = useCoachActivities();
  const deleteAvailability = useDeleteAvailability();
  const params = useParams();
  const availabilities = useAvailabilitySelf(params.date);
  const fullAvailabilities = useAvailabilitySelf();

  const coachActivity = coachActivities.data.find(
    (ca) => ca.id === availability.coachActivityId
  );

  const handleDeleteAvailability = async () => {
    await deleteAvailability(availability.id);
    availabilities.mutate(
      (curr) => curr.filter((a) => a.id !== availability.id),
      false
    );
    fullAvailabilities.mutate(
      (curr) => curr.filter((a) => a.id !== availability.id),
      false
    );
  };

  if (isEditing) {
    return (
      <Card className="py-4">
        <CardContent>
          <EditAvailabilityForm
            availability={availability}
            setIsEditing={setIsEditing}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full relative">
      <CardHeader className="py-4">
        <CardTitle className="text-lg font-bold flex items-center justify-between">
          <div>
            {coachActivity.activity.title}
            {availability.isBooked && (
              <Badge className="ml-4" variant="outline">
                Booked
              </Badge>
            )}
          </div>
          {!availability.isBooked && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        {!availability.isBooked && (
          <div
            onClick={handleDeleteAvailability}
            className="absolute left-[-7px] top-[-7px] text-[var(--primary-white)] bg-[var(--secondary-dark-campfire)] rounded-full w-6 h-6 flex justify-center items-center cursor-pointer"
          >
            <CircleX />
          </div>
        )}
        <div className="flex items-center space-x-2">
          <CalendarDays className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm">
            {getDateWithTimeZone(
              availability.startDateTime,
              availability.timeZone
            )}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm">
            {getTimeWithTimeZone(
              availability.startDateTime,
              availability.timeZone
            )}{' '}
            -{' '}
            {getTimeWithTimeZone(
              availability.endDateTime,
              availability.timeZone
            )}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm">
            {availability.locations.map((loc) => loc.location.name).join(', ')}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default AvailabilityCard;
